import { GatsbyImage } from 'gatsby-plugin-image';
import React from 'react';
import styled from 'styled-components';
import {
  Container,
  Heading,
  InternalLink,
  ExternalLink,
  colors,
  media
} from '../../ui-components';
import { useDefaultImage } from '../insight-cards/insight-card';
import renderRichText from '../../utils/render-rich-text';
import { isInternalLink } from '../../utils/link-handler';
import { truncateText } from '../../utils/truncate-text';

/**
 * Component that renders a card containing a featured insight
 * 
 * @component ElevatedContentCard
 * 
 * @param {object} props
 *
 * @return {JSX.Element}
 */
const ElevatedContentCard = props => {
  const MAX_TITLE_LENGTH = 75;
  const MAX_SNIPPET_LENGTH = 190;

  const { content } = props;

  const defaultImage = useDefaultImage();

  return (
    <>
      <ElevatedContentDesktop id="featuredContent">
        <ElevatedContentPerson
          image={content?.image?.fluid ? content?.image?.fluid : defaultImage.defaultBG.childImageSharp.gatsbyImageData}
          alt={content?.title}
        />
        <ElevatedContentContainer>
          <ElevatedContentTextContainer>
            <ElevatedContentHeaderContainer>
              <Heading level={5}>{truncateText(content.title, MAX_TITLE_LENGTH)}</Heading>
            </ElevatedContentHeaderContainer>
            {content.bodyString ? content.bodyString : truncateText(renderRichText(content.bodyRichJSON, null, 'medium'), MAX_SNIPPET_LENGTH)} 
          </ElevatedContentTextContainer>
          {isInternalLink(content.ctaUrl) ? (
            <InternalLink linkstyle={'primary-link'} hoverUnderline to={content.ctaUrl} withArrow>
              {content.ctaText}
            </InternalLink>
          ) : (
            <ExternalLink linkstyle={'primary-link'} hoverUnderline href={content.ctaUrl} withArrow>
              {content.ctaText}
            </ExternalLink>
          )}
        </ElevatedContentContainer>
      </ElevatedContentDesktop>
      <ElevatedContentMobile>
        <MobileAuthorImage
          image={content?.image?.fluid ? content?.image?.fluid : defaultImage.defaultBG.childImageSharp.gatsbyImageData}
          alt={content?.title}
        />
        <ElevatedContentHeaderContainer>
          <Heading level={5}>{truncateText(content.title, MAX_TITLE_LENGTH)}</Heading>
        </ElevatedContentHeaderContainer>
        {content.bodyString ? content.bodyString : truncateText(renderRichText(content.bodyRichJSON, null, 'medium'), MAX_SNIPPET_LENGTH)}
        <MobileButton linkstyle="primary-button" to={content.ctaUrl}>
          {content.ctaText}
        </MobileButton>
      </ElevatedContentMobile>
    </>
  );
};

const ElevatedContentMobile = styled(Container)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  text-align: center;
  margin-right: 20px;
  margin-left: 20px;
  background-color: ${colors.lightSky};

  @media screen and (width > ${media.mediumTop}) {
    display: none;
  }
`;

const MobileAuthorImage = styled(GatsbyImage)`
  max-height: 96px;
  max-width: 96px;
  border-radius: 100%;
`;

const MobileButton = styled(InternalLink)`
  max-width: fit-content;
`;

const ElevatedContentDesktop = styled.div`
  align-items: flex-end;
  justify-content: center;
  display: flex;
  max-width: 1024px;
  margin: auto;
  padding: 0;
  z-index: 1;

  @media screen and (width <= ${media.mediumTop}) {
    margin: 0;
    display: none;
  }
`;

const ElevatedContentPerson = styled(GatsbyImage)`
  width: 224px;
  height: 220px;
  z-index: 1;
  box-shadow: 0 0 80px 0 rgba(0, 0, 0, 0.06);

  @media screen and (max-width: ${media.mediumTop}) {
    display: none;
  }
`;

const ElevatedContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${colors.lightSky};
  position: relative;
  margin-left: -24px;
  width: 752px;
  min-height: 200px;
  padding: 36px 32px;
  box-shadow: 0 0 80px 0 rgba(0, 0, 0, 0.06);
  z-index: 2;
  flex-shrink: 0;
  align-items: flex-start;
  gap: 20px;

  @media screen and (max-width: ${media.mediumTop}) {
    flex: 1 0 0;
    margin: 0;
    max-width: 100%;
    align-items: center;
    align-self: stretch;
    padding: 40px 16px;
    background: ${colors.secondaryBackground};
  }
`;

const ElevatedContentTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
  padding: 0;

  // Remove the margin that RRT is adding
  p {
    margin: 0;
  }

  @media screen and (max-width: ${media.mediumTop}) {
    gap: 20px;
  }
`;

const ElevatedContentHeaderContainer = styled.div`
  display: flex;
  align-items: center;
  align-self: stretch;
  gap: 12px;

  @media screen and (max-width: ${media.mediumTop}) {
    flex-direction: column;
    max-width: 100%;
    text-align: center;
  }
`;

export default ElevatedContentCard;
